import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import Modal from '../../components/ui/Modal';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import CopyButton from '../../components/ui/CopyButton';
import { roleBadgeClass } from '../../constants/roles';

function fmt(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'short' });
}

function Row({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5
                    border-b border-white/[0.05] last:border-0 text-sm">
      <span className="text-gray-500 flex-shrink-0 w-32">{label}</span>
      <span className="text-gray-200 text-right break-all min-w-0">{children}</span>
    </div>
  );
}


export default function UserDetailModal({ userId, onClose }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/admin/users/${userId}`)
      .then(r => setUser(r.data))
      .catch(() => setError('Failed to load user.'))
      .finally(() => setLoading(false));
  }, [userId]);

  const subStatus = () => {
    if (!user.expires_at) return <span className="text-gray-500">No subscription</span>;
    if (user.is_frozen)   return <span className="text-sky-300">Frozen</span>;
    if (new Date(user.expires_at) < new Date()) return <span className="text-gray-500">Expired</span>;
    return <span className="text-emerald-300">Active</span>;
  };

  return (
    <Modal
      title="Account Details"
      onClose={onClose}
      icon={
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
             strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21v-1a6 6 0 016-6h4a6 6 0 016 6v1" />
        </svg>
      }
    >
      {loading && <div className="py-10 flex justify-center"><LoadingSpinner /></div>}

      {error && <p className="text-center py-10 text-red-300 text-sm">{error}</p>}

      {user && (
        <div>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-brand-gradient border border-brand-400/25
                            flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
              {user.username[0].toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="font-display font-semibold text-white truncate">{user.username}</p>
              <span className={`inline-block mt-1 text-[10px] font-semibold uppercase tracking-wider
                                px-2 py-0.5 rounded border ${roleBadgeClass(user.role)}`}>
                {user.role}
              </span>
            </div>
            {!!user.is_banned && (
              <span className="ml-auto badge-banned flex-shrink-0">Banned</span>
            )}
          </div>

          <div className="surface-sunken px-4">
            <Row label="Email">{user.email}</Row>
            <Row label="Registered">{fmt(user.created_at)}</Row>
            <Row label="Invited by">{user.invited_by_name || '—'}</Row>
            <Row label="Subscription">{subStatus()}</Row>
            {user.expires_at && <Row label="Expires">{fmt(user.expires_at)}</Row>}
            {user.product_name && (
              <Row label="Plan">
                {user.product_name}
                {user.plan_label && <span className="text-gray-500 ml-1">· {user.plan_label}</span>}
              </Row>
            )}
            {user.hwid && (
              <Row label="HWID">
                <span className="inline-flex items-center gap-1">
                  <span className="font-mono text-xs text-gray-400 break-all">{user.hwid}</span>
                  <CopyButton value={user.hwid} variant="icon" />
                </span>
              </Row>
            )}
            {!!user.is_banned && user.ban_reason && (
              <Row label="Ban reason">
                <span className="text-red-300">{user.ban_reason}</span>
              </Row>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
